// src/pages/admin/AdminLiquidaciones.js
//
// Liquidación de Guías: preview + generación + marcado de pago.
// Selecciona período, calcula, revisa por Guía, genera, dispersa.

import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField,
  Button, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  IconButton, Tooltip, Alert, LinearProgress, Grid, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import PaymentsIcon      from '@mui/icons-material/Payments';
import CalculateIcon     from '@mui/icons-material/Calculate';
import SaveIcon          from '@mui/icons-material/Save';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import BlockIcon         from '@mui/icons-material/Block';
import { liquidacionesGuias as api } from '../../api/tesoreriaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const colorEstado = (e) => e==='PAGADA'?'success':e==='CALCULADA'?'info':e==='APROBADA'?'primary':'default';

// Primer y ultimo dia del mes anterior por defecto
const inicioMesAnterior = () => {
  const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1);
  return d.toISOString().substring(0, 10);
};
const finMesAnterior = () => {
  const d = new Date(); d.setDate(0);   // último día del mes anterior
  return d.toISOString().substring(0, 10);
};

export default function AdminLiquidaciones() {
  const { notificar } = useApp();
  const [periodo, setPeriodo] = useState({
    desde: inicioMesAnterior(), hasta: finMesAnterior(),
  });
  const [preview, setPreview] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogPago, setDialogPago] = useState(null);

  const cargarHistorico = async () => {
    try { setHistorico(await api.listar({}) || []); } catch (e) { /* ignore */ }
  };
  useEffect(() => { cargarHistorico(); }, []);

  const calcular = async () => {
    setCargando(true);
    try {
      const r = await api.calcular(periodo.desde, periodo.hasta);
      setPreview(r);
      notificar(`Calculado: ${r.totalGuias} Guías con comisión.`, 'success');
    } catch (e) {
      notificar(e.mensajeUsuario || 'Error calculando.', 'error');
    } finally { setCargando(false); }
  };

  const generar = async () => {
    if (!preview || preview.totalGuias === 0) {
      notificar('Calcula primero un período con comisiones.', 'warning'); return;
    }
    if (!window.confirm(`Generar ${preview.totalGuias} liquidaciones? Reemplaza las abiertas del mismo período.`)) return;
    try {
      const r = await api.generar(periodo.desde, periodo.hasta);
      notificar(r.mensaje, 'success');
      cargarHistorico();
    } catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
  };

  const anular = async (l) => {
    const motivo = window.prompt('Motivo de anulación:');
    if (!motivo) return;
    try { await api.anular(l.liquidacionId, motivo); notificar('Anulada.', 'success'); cargarHistorico(); }
    catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
  };

  const totalesPreview = preview?.liquidaciones?.reduce((acc, l) => ({
    bruto: acc.bruto + l.bruto, rete: acc.rete + l.montoRetefuente, neto: acc.neto + l.neto,
  }), { bruto: 0, rete: 0, neto: 0 });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <PaymentsIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Liquidaciones a Guías</Typography>
          <Typography variant="body2" color="text.secondary">
            Comisiones por facturas pagadas en el período.
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Solo se comisiona sobre facturas SaaS con estado PAGADA. Cero prorrateo.
        Retención en la fuente se descuenta del pago.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" size="small" label="Desde"
                         InputLabelProps={{ shrink: true }}
                         value={periodo.desde}
                         onChange={e => setPeriodo({ ...periodo, desde: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" size="small" label="Hasta"
                         InputLabelProps={{ shrink: true }}
                         value={periodo.hasta}
                         onChange={e => setPeriodo({ ...periodo, hasta: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" startIcon={<CalculateIcon />} onClick={calcular} disabled={cargando}>
                  Calcular
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={generar}
                        disabled={!preview || preview.totalGuias === 0}>
                  Generar liquidaciones
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {cargando && <LinearProgress />}

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontFamily="Fraunces" gutterBottom>
              Preview del cálculo
            </Typography>
            {preview.totalGuias === 0 ? (
              <Alert severity="warning">
                No hay comisiones para este período. Verifica que existan facturas SaaS PAGADAS
                y que sus empresas tengan GuiaId asignado.
              </Alert>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Paper label="Bruto total" v={fmtCOP(totalesPreview.bruto)} c="text.primary" /></Grid>
                  <Grid item xs={4}><Paper label="Retención" v={fmtCOP(totalesPreview.rete)} c="error.main" /></Grid>
                  <Grid item xs={4}><Paper label="Neto a pagar" v={fmtCOP(totalesPreview.neto)} c="success.main" /></Grid>
                </Grid>
                {preview.liquidaciones.map(l => (
                  <Accordion key={l.guia.guiaId}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Grid container alignItems="center">
                        <Grid item xs={5}><b>{l.guia.nombreCompleto}</b>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {l.detalles.length} afiliados · {l.guia.porcentajeComision}% comisión
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>{fmtCOP(l.bruto)}</Grid>
                        <Grid item xs={2} sx={{ color: 'error.main' }}>-{fmtCOP(l.montoRetefuente)}</Grid>
                        <Grid item xs={3} sx={{ color: 'success.main', fontWeight: 700 }}>{fmtCOP(l.neto)}</Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Afiliado</TableCell><TableCell>Factura</TableCell>
                            <TableCell>Producto</TableCell><TableCell align="right">Base</TableCell>
                            <TableCell align="right">%</TableCell><TableCell align="right">Comisión</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {l.detalles.map((d,i) => (
                            <TableRow key={i}>
                              <TableCell>{d.nombreComercial || d.empresa}</TableCell>
                              <TableCell><Typography variant="caption">{d.numeroFactura}</Typography></TableCell>
                              <TableCell>{d.producto}</TableCell>
                              <TableCell align="right">{fmtCOP(d.baseGravable)}</TableCell>
                              <TableCell align="right">{d.porcentajeComision}%</TableCell>
                              <TableCell align="right"><b>{fmtCOP(d.comision)}</b></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" fontFamily="Fraunces" gutterBottom sx={{ mt: 4 }}>
        Histórico de liquidaciones
      </Typography>
      {historico.length === 0 ? <Alert severity="info">Sin liquidaciones generadas todavía.</Alert> : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Guía</TableCell><TableCell>Período</TableCell>
                <TableCell align="right">Bruto</TableCell>
                <TableCell align="right">Retención</TableCell>
                <TableCell align="right">Neto</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell>Pagada</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historico.map(l => (
                <TableRow key={l.liquidacionId} hover>
                  <TableCell>{l.guia}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {l.periodoDesde?.substring(0,10)} → {l.periodoHasta?.substring(0,10)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtCOP(l.bruto)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.montoRetefuente)}</TableCell>
                  <TableCell align="right"><b>{fmtCOP(l.neto)}</b></TableCell>
                  <TableCell align="center">
                    <Chip label={l.estado} size="small" color={colorEstado(l.estado)} />
                  </TableCell>
                  <TableCell>{l.fechaPago ? l.fechaPago.substring(0,10) : '—'}</TableCell>
                  <TableCell align="right">
                    {l.estado !== 'PAGADA' && l.estado !== 'ANULADA' && (
                      <>
                        <Tooltip title="Marcar pagada">
                          <IconButton size="small" color="success" onClick={() => setDialogPago(l)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Anular">
                          <IconButton size="small" color="error" onClick={() => anular(l)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <DialogPagoLiq liquidacion={dialogPago}
                     onCerrar={() => setDialogPago(null)}
                     onPagada={() => { setDialogPago(null); cargarHistorico(); }} />
    </Container>
  );
}

function Paper({ label, v, c }) {
  return (
    <Card sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ color: c }}>{v}</Typography>
    </Card>
  );
}

function DialogPagoLiq({ liquidacion, onCerrar, onPagada }) {
  const { notificar } = useApp();
  const [f, setF] = useState({ medioPago: 'TRANSFERENCIA', referenciaPago: '',
                                comprobanteUrl: '', notas: '' });
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      await api.marcarPagada(liquidacion.liquidacionId, f);
      notificar('Marcada como pagada.', 'success');
      onPagada();
    } catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
    finally { setG(false); }
  };
  return (
    <Dialog open={!!liquidacion} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar pago al Guía</DialogTitle>
      <DialogContent>
        {liquidacion && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>{liquidacion.guia}</b> · Neto {fmtCOP(liquidacion.neto)}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField select fullWidth label="Medio de pago" value={f.medioPago}
                     onChange={e => setF({ ...f, medioPago: e.target.value })}>
            <MenuItem value="WOMPI_DISPERSION">Wompi Dispersión</MenuItem>
            <MenuItem value="TRANSFERENCIA">Transferencia bancaria</MenuItem>
            <MenuItem value="NEQUI">Nequi</MenuItem>
            <MenuItem value="DAVIPLATA">Daviplata</MenuItem>
            <MenuItem value="OTRO">Otro</MenuItem>
          </TextField>
          <TextField fullWidth label="Referencia" value={f.referenciaPago}
                     onChange={e => setF({ ...f, referenciaPago: e.target.value })} />
          <TextField fullWidth label="URL comprobante" value={f.comprobanteUrl}
                     onChange={e => setF({ ...f, comprobanteUrl: e.target.value })} />
          <TextField fullWidth multiline rows={2} label="Notas" value={f.notas}
                     onChange={e => setF({ ...f, notas: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>Confirmar pago</Button>
      </DialogActions>
    </Dialog>
  );
}
